import React from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Brand} from '@vio/core/src/brand/types'
import {
  getLocalizedContactUrl,
  getLocalizedFooterUrl,
  getLocalizedHelpUrl
} from '@vio/core/src/brand/utils'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {useDeviceLayout} from '../../../context/deviceLayout'
import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Divider} from '../Divider'
import {Text} from '../Text'
import {FooterLink} from './FooterLinkButton'
import {SocialMediaLinksWidget} from './SocialMediaLinksWidget'

type ForcedLayout = 'compact' | 'wide'

export interface Props {
  /** Brand configuration. It can be one of our brands or a white-label brand. Determines which links and information to display. */
  brand: Brand
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Which layout to force. If not set, the component will use the device layout to determine which layout to display. */
  forcedLayout?: ForcedLayout
  /** The component to render in the widget slot (e.g. Trustpilot) */
  widget?: React.ReactNode
  /** The component to render in the getTheApp slot */
  getTheAppSlot?: React.ReactNode
  /** The component to render in the cookie preference link slot */
  CookiePreferencesLinkSlot?: React.ReactNode
  /** The component to render in the how-to-cancel link slot (e.g. for IL market compliance) */
  howToCancelLinkSlot?: React.ReactNode
  /** The language code to use for the footer links */
  languageCode?: string
  /** Whether to show the divider above the footer */
  withTopDivider?: boolean
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${cssTheme.colors.background.neutral.primary};
`

export const FooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${cssTheme.layout.spacing.s500};

  &.--compact {
    ${linariaMq.desktopXs} {
      margin: ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s500}
        ${cssTheme.layout.spacing.s500};
    }
  }

  &.--wide {
    margin: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
  }
`

export const FooterCopyright = styled(Text)`
  white-space: nowrap;
`

export const FooterNav = styled.nav`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: ${cssTheme.layout.spacing.s500};
  color: ${cssTheme.colors.content.neutral.primary};
  ${cssTheme.typography.bodyXS};
`

export const FooterNavList = styled.ul<{layoutIsCompact: boolean}>`
  list-style-type: none;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({layoutIsCompact}) =>
    layoutIsCompact ? 'center' : 'flex-start'};
  align-items: center;
  width: 100%;
  padding: ${cssTheme.layout.spacing.s300} 0;
  gap: ${cssTheme.layout.spacing.s500};
  row-gap: ${cssTheme.layout.spacing.s300};
`

export const FooterTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`

export const FooterWidgetsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-shrink: 0;
  gap: ${cssTheme.layout.spacing.s600};
  margin: ${cssTheme.layout.spacing.s250} 0;
`
export const CenterDividerWrapper = styled.div`
  align-self: stretch;
  margin-top: ${cssTheme.layout.spacing.s500};
  margin-bottom: ${cssTheme.layout.spacing.s500};
`

export const Footer = ({
  brand,
  widget,
  className,
  forcedLayout,
  getTheAppSlot,
  CookiePreferencesLinkSlot,
  howToCancelLinkSlot,
  languageCode,
  withTopDivider = true
}: Props) => {
  const {isDesktopMd, isDesktopLg, isDesktop} = useDeviceLayout()

  const layoutIsCompact =
    forcedLayout === undefined
      ? !(isDesktopMd || isDesktopLg)
      : forcedLayout === 'compact'

  const {showSocialMediaLinks} = brand.capabilities.shell

  const shouldRenderWidgetsWrapper = !!widget || showSocialMediaLinks
  const shouldRenderGetTheApp = !!getTheAppSlot && !isDesktop

  const currentYear = new Date().getFullYear()
  return (
    <Wrapper className={className}>
      {withTopDivider && <Divider />}
      <FooterWrapper className={cx(layoutIsCompact ? '--compact' : '--wide')}>
        {(shouldRenderWidgetsWrapper || shouldRenderGetTheApp) && (
          <FooterTop>
            {shouldRenderWidgetsWrapper && (
              <FooterWidgetsWrapper>
                {widget}
                {showSocialMediaLinks && !layoutIsCompact && (
                  <SocialMediaLinksWidget brand={brand} />
                )}
              </FooterWidgetsWrapper>
            )}
            {shouldRenderGetTheApp && getTheAppSlot}

            {widget && layoutIsCompact && (
              <CenterDividerWrapper>
                <Divider />
              </CenterDividerWrapper>
            )}
          </FooterTop>
        )}

        <FooterNav>
          <FooterNavList layoutIsCompact={layoutIsCompact}>
            <li>
              <FooterCopyright variant="bodyXS">
                © {currentYear} {brand.name}
              </FooterCopyright>
            </li>
            {brand.links.aboutUsUrl && (
              <li>
                <FooterLink href={brand.links.aboutUsUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="AboutUs"
                    id="about"
                    defaultMessage="About"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.careersUrl && (
              <li>
                <FooterLink href={brand.links.careersUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="Careers"
                    id="careers"
                    defaultMessage="Careers"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.developersUrl && (
              <li>
                <FooterLink href={brand.links.developersUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="Affiliates"
                    id="affiliates"
                    defaultMessage="Affiliates"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.blogUrl && (
              <li>
                <FooterLink href={brand.links.blogUrl} target="_blank">
                  <FormattedMessageWrapper
                    dataId="Blog"
                    id="blog"
                    defaultMessage="Blog"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.helpUrl && (
              <li>
                <FooterLink
                  href={getLocalizedHelpUrl(brand, languageCode)}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="Help"
                    id="help"
                    defaultMessage="Help"
                  />
                </FooterLink>
              </li>
            )}
            {Boolean(CookiePreferencesLinkSlot) && (
              <>{CookiePreferencesLinkSlot}</>
            )}
            {Boolean(howToCancelLinkSlot) && <>{howToCancelLinkSlot}</>}
            {brand.links.cookiePolicyUrl && (
              <li>
                <FooterLink
                  href={getLocalizedFooterUrl(
                    brand.links.cookiePolicyUrl,
                    languageCode
                  )}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="CookiePolicy"
                    id="cookiePolicy"
                    defaultMessage="Cookie policy"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.privacyPolicyUrl && (
              <li>
                <FooterLink
                  href={getLocalizedFooterUrl(
                    brand.links.privacyPolicyUrl,
                    languageCode
                  )}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="PrivacyPolicy"
                    id="privacyPolicy"
                    defaultMessage="Privacy policy"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.termsOfUseUrl && (
              <li>
                <FooterLink
                  href={getLocalizedFooterUrl(
                    brand.links.termsOfUseUrl,
                    languageCode
                  )}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="TermsOfUse"
                    id="termsOfUse"
                    defaultMessage="Terms of use"
                  />
                </FooterLink>
              </li>
            )}
            {brand.links.contactUrl && (
              <li>
                <FooterLink
                  href={getLocalizedContactUrl(brand, languageCode)}
                  target="_blank"
                >
                  <FormattedMessageWrapper
                    dataId="Contact"
                    id="contact"
                    defaultMessage="Contact"
                  />
                </FooterLink>
              </li>
            )}
            {isDesktop && getTheAppSlot && <li>{getTheAppSlot}</li>}
            {showSocialMediaLinks && layoutIsCompact && (
              <li>
                <SocialMediaLinksWidget brand={brand} />
              </li>
            )}
          </FooterNavList>
        </FooterNav>
      </FooterWrapper>
    </Wrapper>
  )
}
